// ============================================================
// xFreeScan by XCloud Solutions - Scan Detail Page (Light Theme)
// Shows real-time progress with per-module tracking
// ============================================================
import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { scansApi } from '../../lib/api';
import AuraChat from '../../components/AuraChat';
import ScanModeBadge from '../../components/ScanModeBadge';
import ValidationBadge from '../../components/ValidationBadge';
import {
  Shield, Target, Clock, ChevronDown, ChevronUp, AlertTriangle,
  ExternalLink, FileText, RefreshCw, Loader, CheckCircle,
  Globe, Lock, Mail, Cpu, Radio, Bug, Network
} from 'lucide-react';

// Module display info
const MODULE_INFO: Record<string, { label: string; icon: any; description: string; deep?: boolean }> = {
  headers: { label: 'Headers HTTP', icon: Globe, description: 'Verificando headers de seguridad...' },
  ssl: { label: 'SSL/TLS', icon: Lock, description: 'Analizando certificado y configuracion TLS...' },
  dns: { label: 'DNS & Email', icon: Mail, description: 'Revisando SPF, DKIM, DMARC y configuracion DNS...' },
  tech: { label: 'Tecnologias', icon: Cpu, description: 'Detectando tecnologias, CMS y frameworks...' },
  ports: { label: 'Puertos', icon: Radio, description: 'Escaneando puertos HTTP abiertos...' },
  web: { label: 'Vulnerabilidades Web', icon: Bug, description: 'Buscando archivos sensibles y vulnerabilidades OWASP...' },
  subdomain: { label: 'Subdominios', icon: Network, description: 'Descubriendo subdominios via Certificate Transparency y DNS...' },
  nmap: { label: 'Nmap (Puertos Profundo)', icon: Radio, description: 'Escaneo completo de los top 1000 puertos TCP con deteccion de servicios...', deep: true },
  nuclei: { label: 'Nuclei (CVEs)', icon: Bug, description: 'Buscando vulnerabilidades conocidas (CVEs) con templates actualizados...', deep: true },
  nikto: { label: 'Nikto (Web Server)', icon: Bug, description: 'Escaneo profundo del servidor web en busca de misconfigurations...', deep: true },
  subfinder: { label: 'Subfinder + httpx', icon: Network, description: 'Descubrimiento avanzado de subdominios y probing de servicios activos...', deep: true },
  testssl: { label: 'testssl.sh (SSL)', icon: Lock, description: 'Analisis exhaustivo de la configuracion SSL/TLS...', deep: true },
};

export default function ScanDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [scan, setScan] = useState<any>(null);
  const [findings, setFindings] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedFinding, setExpandedFinding] = useState<string | null>(null);
  const [severityFilter, setSeverityFilter] = useState('');
  const [retestResults, setRetestResults] = useState<Record<string, any>>({});
  const [retestLoading, setRetestLoading] = useState<Record<string, boolean>>({});
  const [prevStatus, setPrevStatus] = useState<string>('');
  const [scanJustCompleted, setScanJustCompleted] = useState(false);

  useEffect(() => {
    if (id) loadScan();
  }, [id]);

  useEffect(() => {
    if (id && (scan?.status === 'completed' || scan?.status === 'running')) loadFindings();
  }, [id, scan?.status, scan?.progress, severityFilter]);

  // Detect scan completion: running → completed
  useEffect(() => {
    if (prevStatus === 'running' && scan?.status === 'completed') {
      setScanJustCompleted(true);
    }
    if (scan?.status) setPrevStatus(scan.status);
  }, [scan?.status]);

  // Auto-refresh for running scans (every 3 seconds for snappier updates)
  useEffect(() => {
    if (scan?.status === 'running' || scan?.status === 'queued') {
      const interval = setInterval(() => loadScan(), 3000);
      return () => clearInterval(interval);
    }
  }, [scan?.status]);

  const loadScan = async () => {
    try {
      const response = await scansApi.get(id!);
      if (response.success) setScan(response.data);
    } catch (error) {
      console.error('Error loading scan:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadFindings = async () => {
    try {
      const response = await scansApi.getFindings(id!, {
        severity: severityFilter || undefined,
      });
      if (response.success) setFindings(response.data);
    } catch (error) {
      console.error('Error loading findings:', error);
    }
  };

  const handleRetest = async (findingId: string) => {
    setRetestLoading(prev => ({ ...prev, [findingId]: true }));
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL || 'https://xfreescan-api.aforero.workers.dev'}/api/scans/${id}/findings/${findingId}/retest`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('xfreescan_token')}`,
          'Content-Type': 'application/json',
        },
      });
      const data = await response.json();
      if (data.success) {
        setRetestResults(prev => ({ ...prev, [findingId]: data.data.retest_result }));
      }
    } catch (err) {
      setRetestResults(prev => ({ ...prev, [findingId]: { status: 'error', details: 'Error de conexion' } }));
    } finally {
      setRetestLoading(prev => ({ ...prev, [findingId]: false }));
    }
  };

  const getSeverityBadge = (severity: string) => {
    const map: Record<string, string> = {
      critical: 'badge-critical',
      high: 'badge-high',
      medium: 'badge-medium',
      low: 'badge-low',
      info: 'badge-info',
    };
    const labels: Record<string, string> = {
      critical: 'Critico',
      high: 'Alto',
      medium: 'Medio',
      low: 'Bajo',
      info: 'Info',
    };
    return <span className={map[severity] || 'badge-info'}>{labels[severity] || severity}</span>;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-purple-600 border-t-transparent" />
      </div>
    );
  }

  if (!scan) {
    return (
      <div className="text-center py-20">
        <AlertTriangle className="mx-auto h-12 w-12 text-gray-400" />
        <p className="mt-4 text-gray-500">Escaneo no encontrado</p>
      </div>
    );
  }

  const isRunning = scan.status === 'running' || scan.status === 'queued';

  // Parse modules info for progress display
  const modulesQueue: string[] = scan.modules_queue ? JSON.parse(scan.modules_queue) : [];
  const completedModules: string[] = scan.partial?.modules_completed || [];
  const currentModule = scan.current_module;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="card">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-4">
            <div className={`rounded-xl p-3 ${
              scan.status === 'completed' ? 'bg-emerald-50' :
              scan.status === 'failed' ? 'bg-red-50' :
              isRunning ? 'bg-purple-50 scan-pulse' : 'bg-gray-50'
            }`}>
              {isRunning ? (
                <Loader className="h-8 w-8 text-purple-600 animate-spin" />
              ) : scan.status === 'failed' ? (
                <AlertTriangle className="h-8 w-8 text-red-600" />
              ) : (
                <Target className={`h-8 w-8 ${scan.status === 'completed' ? 'text-emerald-600' : 'text-gray-600'}`} />
              )}
            </div>
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-xl font-bold text-gray-900">{scan.target}</h1>
                <ScanModeBadge mode={(scan as any).scan_mode} size="sm" />
              </div>
              <p className="text-sm text-gray-500">{scan.nombre}</p>
            </div>
          </div>
          <div className="flex gap-3">
            {scan.status === 'completed' && (
              <>
                <Link to={`/app/reports/${scan.id}`} className="btn-primary text-sm">
                  <FileText className="h-4 w-4" />
                  Ver Reporte
                </Link>
                <button
                  onClick={() => navigate(`/app/scans/new?target=${encodeURIComponent(scan.target)}`)}
                  className="btn-secondary text-sm"
                >
                  <RefreshCw className="h-4 w-4" /> Re-escanear
                </button>
              </>
            )}
            <button onClick={loadScan} className="btn-secondary text-sm">
              <RefreshCw className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Failed scan message */}
        {scan.status === 'failed' && (
          <div className="mt-4 rounded-lg bg-red-50 border border-red-200 p-4">
            <p className="text-sm text-red-700 font-medium">El escaneo fallo</p>
            {scan.error_message && (
              <p className="text-xs text-red-600 mt-1">{scan.error_message}</p>
            )}
          </div>
        )}

        {/* Module-by-module progress for running scans */}
        {isRunning && (
          <div className="mt-6">
            <div className="flex items-center justify-between text-sm mb-3">
              <span className="text-gray-500">Progreso del escaneo</span>
              <span className="text-purple-700 font-medium">{scan.progress}%</span>
            </div>
            <div className="h-3 w-full rounded-full bg-gray-200">
              <div
                className="h-full rounded-full bg-gradient-to-r from-purple-600 to-purple-500 transition-all duration-1000"
                style={{ width: `${scan.progress}%` }}
              />
            </div>

            {/* Module list with status */}
            {modulesQueue.length > 0 && (
              <div className="mt-4 space-y-2">
                {modulesQueue.map((mod, idx) => {
                  const info = MODULE_INFO[mod] || { label: mod, icon: Shield, description: '' };
                  const IconComp = info.icon;
                  const isCompleted = completedModules.includes(mod);
                  const isCurrent = currentModule === mod;
                  // When current_module is 'deep-scan' (legacy), mark first pending deep module
                  const isDeepScanGeneric = currentModule === 'deep-scan';
                  const isFirstPendingDeep = isDeepScanGeneric && (info as any).deep && !isCompleted &&
                    !modulesQueue.slice(0, idx).some(m => {
                      const mi = MODULE_INFO[m];
                      return mi && (mi as any).deep && !completedModules.includes(m);
                    });
                  const isActive = isCurrent || isFirstPendingDeep;
                  const isPending = !isCompleted && !isActive;

                  // Show separator before first deep module
                  const isFirstDeep = (info as any).deep && !modulesQueue.slice(0, idx).some(m => (MODULE_INFO[m] as any)?.deep);

                  return (
                    <div key={mod}>
                      {isFirstDeep && (
                        <div className="flex items-center gap-2 py-2 px-1">
                          <div className="flex-1 h-px bg-orange-200" />
                          <span className="text-[10px] font-semibold text-orange-500 uppercase tracking-wider">
                            Escaneo Profundo (EC2)
                          </span>
                          <div className="flex-1 h-px bg-orange-200" />
                        </div>
                      )}
                      <div
                        className={`flex items-center gap-3 rounded-lg px-3 py-2 transition-all ${
                          isActive ? 'bg-purple-50 border border-purple-200' :
                          isCompleted ? 'bg-emerald-50 border border-emerald-100' :
                          'bg-gray-50 border border-transparent'
                        }`}
                      >
                        <div className={`flex-shrink-0 ${
                          isActive ? 'text-purple-600' :
                          isCompleted ? 'text-emerald-500' :
                          'text-gray-300'
                        }`}>
                          {isActive ? (
                            <Loader className="h-5 w-5 animate-spin" />
                          ) : isCompleted ? (
                            <CheckCircle className="h-5 w-5" />
                          ) : (
                            <IconComp className="h-5 w-5" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-medium ${
                            isActive ? 'text-purple-700' :
                            isCompleted ? 'text-emerald-700' :
                            'text-gray-400'
                          }`}>
                            {info.label}
                            {(info as any).deep && (
                              <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-600 ring-1 ring-orange-200 font-semibold">EC2</span>
                            )}
                          </p>
                          {isActive && (
                            <p className="text-xs text-purple-500 mt-0.5">{info.description}</p>
                          )}
                        </div>
                        {isCompleted && scan.partial?.findings_by_module?.[mod] !== undefined && (
                          <span className="text-xs text-emerald-600 font-medium">
                            {scan.partial.findings_by_module[mod]} hallazgos
                          </span>
                        )}
                        {isPending && (
                          <span className="text-xs text-gray-300">Pendiente</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="mt-3 flex items-center gap-2 text-xs text-gray-400">
              <RefreshCw className="h-3 w-3 animate-spin" />
              {modulesQueue.some(m => (MODULE_INFO[m] as any)?.deep) ? (
                <span>Todos los modulos se ejecutan en el servidor EC2. Los profundos (Nmap, Nuclei, Nikto, testssl) pueden tomar 3-8 minutos. Actualizacion automatica cada 3s.</span>
              ) : (
                <span>Cada modulo se ejecuta en el servidor EC2. Actualizacion automatica cada 3 segundos.</span>
              )}
            </div>
          </div>
        )}

        {/* Summary for completed scans */}
        {scan.status === 'completed' && scan.score != null && (
          <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-4 lg:grid-cols-7">
            <div className="text-center">
              <p className={`text-3xl font-bold ${
                scan.grade?.startsWith('A') ? 'grade-a' :
                scan.grade?.startsWith('B') ? 'grade-b' :
                scan.grade?.startsWith('C') ? 'grade-c' :
                scan.grade?.startsWith('D') ? 'grade-d' : 'grade-f'
              }`}>
                {scan.grade}
              </p>
              <p className="text-xs text-gray-500 mt-1">Calificacion</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-gray-900">{scan.score}</p>
              <p className="text-xs text-gray-500 mt-1">Score /100</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-gray-900">{scan.total_findings}</p>
              <p className="text-xs text-gray-500 mt-1">Total</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-red-600">{scan.critical || 0}</p>
              <p className="text-xs text-gray-500 mt-1">Criticos</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-orange-500">{scan.high || 0}</p>
              <p className="text-xs text-gray-500 mt-1">Altos</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-yellow-600">{scan.medium || 0}</p>
              <p className="text-xs text-gray-500 mt-1">Medios</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-blue-600">{scan.low || 0}</p>
              <p className="text-xs text-gray-500 mt-1">Bajos</p>
            </div>
          </div>
        )}
      </div>

      {/* Findings - show for completed AND running scans (partial results) */}
      {(scan.status === 'completed' || (isRunning && findings.length > 0)) && (
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              Hallazgos ({findings.length})
              {isRunning && <span className="text-sm font-normal text-gray-400 ml-2">(parcial)</span>}
            </h2>
            <div className="flex gap-2">
              {['', 'critical', 'high', 'medium', 'low', 'info'].map((s) => (
                <button
                  key={s}
                  onClick={() => setSeverityFilter(s)}
                  className={`text-xs px-2.5 py-1 rounded-full transition-colors ${
                    severityFilter === s
                      ? s === 'critical' ? 'badge-critical' :
                        s === 'high' ? 'badge-high' :
                        s === 'medium' ? 'badge-medium' :
                        s === 'low' ? 'badge-low' :
                        s === 'info' ? 'badge-info' :
                        'bg-purple-100 text-purple-700 ring-1 ring-purple-200'
                      : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                  }`}
                >
                  {s === '' ? 'Todos' :
                   s === 'critical' ? 'Critico' :
                   s === 'high' ? 'Alto' :
                   s === 'medium' ? 'Medio' :
                   s === 'low' ? 'Bajo' : 'Info'}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-3">
            {findings.map((finding) => (
              <div
                key={finding.id}
                className="rounded-xl border border-gray-200 bg-white overflow-hidden"
              >
                <button
                  onClick={() => setExpandedFinding(expandedFinding === finding.id ? null : finding.id)}
                  className="flex w-full items-center gap-3 p-4 text-left hover:bg-gray-50 transition-colors"
                >
                  {getSeverityBadge(finding.severity)}
                  <span className="flex-1 text-sm font-medium text-gray-900">{finding.title}</span>
                  <ValidationBadge level={(finding as any).validation_level} />
                  <span className="badge bg-gray-100 text-gray-500 text-[10px]">{finding.scan_type}</span>
                  {expandedFinding === finding.id ? (
                    <ChevronUp className="h-4 w-4 text-gray-400" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-gray-400" />
                  )}
                </button>

                {expandedFinding === finding.id && (
                  <div className="border-t border-gray-200 p-4 space-y-4 bg-gray-50">
                    <ValidationBadge level={(finding as any).validation_level} variant="full" />

                    <div>
                      <h4 className="text-xs font-semibold uppercase text-gray-500 mb-1">Descripcion</h4>
                      <p className="text-sm text-gray-700">{finding.description}</p>
                    </div>

                    {finding.evidence && (
                      <div>
                        <h4 className="text-xs font-semibold uppercase text-gray-500 mb-1">Evidencia</h4>
                        <pre className="rounded-lg bg-gray-900 p-3 text-xs text-gray-300 font-mono overflow-x-auto">
                          {finding.evidence}
                        </pre>
                      </div>
                    )}

                    <div>
                      <h4 className="text-xs font-semibold uppercase text-gray-500 mb-1">Remediacion</h4>
                      <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3">
                        <p className="text-sm text-emerald-700">{finding.remediation}</p>
                      </div>
                    </div>

                    {/* Compliance tags */}
                    {finding.compliance && typeof finding.compliance === 'object' && Object.keys(finding.compliance).length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {Object.entries(finding.compliance).map(([fw, ctrls]: [string, any]) =>
                          (ctrls as string[]).slice(0, 2).map((ctrl: string, ci: number) => (
                            <span key={`${fw}-${ci}`} className="text-[9px] bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded font-medium">
                              {fw}: {ctrl}
                            </span>
                          ))
                        )}
                      </div>
                    )}

                    {/* Retest button */}
                    {scan?.status === 'completed' && finding.severity !== 'info' && (
                      <div className="mt-3 flex items-center gap-3">
                        <button
                          onClick={() => handleRetest(finding.id)}
                          disabled={retestLoading[finding.id]}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-purple-300 text-purple-700 bg-purple-50 hover:bg-purple-100 disabled:opacity-50 transition-colors"
                        >
                          {retestLoading[finding.id] ? (
                            <><div className="h-3 w-3 animate-spin rounded-full border-2 border-purple-600 border-t-transparent" /> Verificando...</>
                          ) : (
                            <><RefreshCw className="h-3 w-3" /> Verificar Remediacion</>
                          )}
                        </button>
                        {retestResults[finding.id] && (
                          <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold ${
                            retestResults[finding.id].status === 'remediated' ? 'bg-emerald-100 text-emerald-800' :
                            retestResults[finding.id].status === 'persists' ? 'bg-red-100 text-red-800' :
                            'bg-yellow-100 text-yellow-800'
                          }`}>
                            {retestResults[finding.id].status === 'remediated' ? 'Remediado' :
                             retestResults[finding.id].status === 'persists' ? 'Persiste' : 'Inconcluso'}
                            <span className="font-normal ml-1 text-[10px]">{retestResults[finding.id].details?.substring(0, 80)}</span>
                          </span>
                        )}
                      </div>
                    )}

                    {finding.references?.length > 0 && (
                      <div>
                        <h4 className="text-xs font-semibold uppercase text-gray-500 mb-1">Referencias</h4>
                        <div className="flex flex-wrap gap-2">
                          {finding.references.map((ref: string, i: number) => (
                            <a
                              key={i}
                              href={ref}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-xs text-purple-600 hover:text-purple-500"
                            >
                              <ExternalLink className="h-3 w-3" />
                              {(() => { try { return new URL(ref).hostname; } catch { return ref; } })()}
                            </a>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="text-xs text-gray-400">
                      Componente afectado: {finding.affected_component}
                    </div>
                  </div>
                )}
              </div>
            ))}

            {findings.length === 0 && (
              <div className="text-center py-8">
                <CheckCircle className="mx-auto h-10 w-10 text-emerald-500" />
                <p className="mt-2 text-gray-500">
                  {severityFilter ? 'No se encontraron hallazgos con este filtro' : 'No se encontraron hallazgos'}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
      {/* AURA Chat — appears when scan is completed, auto-opens on first completion */}
      {scan?.status === 'completed' && id && (
        <AuraChat
          scanId={id}
          autoOpen={scanJustCompleted}
          proactiveMessage={scanJustCompleted ? `Escaneo de **${scan.target}** completado.\n\nDetecte ${findings.filter(f => f.severity === 'critical').length} hallazgos criticos y ${findings.filter(f => f.severity === 'high').length} altos.\n\nPregunteme que arreglar primero, cuanto le costaria una brecha, o sobre cumplimiento normativo.` : undefined}
        />
      )}
    </div>
  );
}
